import { XIntegration } from 'api/clients/ExternalIntegrationsClient'
import Styles from './XPreview.module.scss'
import { Bookmark, Like, Reply, Repost, Share, Views } from './icons'
import { XPhotoBox } from './XPhotoBox'
import { ReactNode, RefObject, useEffect, useRef, useState } from 'react'
import { MoreButton } from '../components/MoreButton/MoreButton'
import { useTruncateText } from '../hooks/useTruncateText'
import { getIntegrationDetails } from '../getIntegrationDetails'
import { PollingPreview } from '../components/PollingPreview/PollingPreview'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Loading } from '../components/Loading/Loading'
import { HighlightContentOptions, highlightContent } from '../highlightContent'
import { ConnectButton } from 'components/ArticlePostPreview/ConnectButton/ConnectButton'
import { MediaObject } from 'types'
import { Button } from 'blaze-ui/Button'
import { Edit1 } from 'blaze-ui/icons'
import cx from 'classnames'
import * as CSS from 'csstype'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { EditCaptionOverlay } from '../components/EditCaptionOverlay/EditCaptionOverlay'
import { ScheduledPostShow } from 'api/clients/ScheduledPostsClient'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { CaptionSkeleton } from '../components/CaptionSkeleton/CaptionSkeleton'

interface XPreviewProps {
  integration?: XIntegration
  content: string
  mediaObjects?: MediaObject[]
  loading?: boolean
  hideConnectButton?: boolean
  onEdit?: () => void
  containerClassName?: string
  customPreviewContent?: ReactNode
  maxWidth?: number | null
  showVideoControls?: boolean
  isActive?: boolean
  isInsertingMedia?: boolean
  isTransitioning?: boolean
  isGenerating?: boolean
  showHoverActionOverlay?: boolean
  almanacFileKey?: string
  scheduledPost?: ScheduledPostShow
  onClose?: () => void
  alwaysTruncateCaption?: boolean
  postPreviewHeaderRef?: RefObject<HTMLDivElement>
  postPreviewContentRef?: RefObject<HTMLDivElement>
  postPreviewFooterRef?: RefObject<HTMLDivElement>
  onSeeMoreToggle?: (expanded: boolean) => void
  activePlatform?: ExternalIntegrationEnum
}

export const XPreview = ({
  mediaObjects,
  content,
  integration,
  loading,
  hideConnectButton,
  onEdit,
  containerClassName,
  customPreviewContent,
  showVideoControls,
  maxWidth = 550,
  isActive = true,
  isInsertingMedia = false,
  isTransitioning = false,
  isGenerating = false,
  showHoverActionOverlay = false,
  almanacFileKey,
  scheduledPost,
  onClose,
  alwaysTruncateCaption = false,
  postPreviewHeaderRef,
  postPreviewFooterRef,
  onSeeMoreToggle,
  activePlatform,
}: XPreviewProps) => {
  const { name, picture, username } = getIntegrationDetails(integration)
  const contentRef = useRef<HTMLDivElement>(null)
  const [imagesLoading, setImagesLoading] = useState(false)
  const { isMobile } = useResponsiveDesign()
  const { isTruncated, setIsTruncated, shouldShowMoreButton, textRef } = useTruncateText({
    content,
    truncatedByDefault: alwaysTruncateCaption || (!isMobile && (!!mediaObjects?.length || !!customPreviewContent)),
  })

  useEffect(() => {
    if (activePlatform && activePlatform !== ExternalIntegrationEnum.X && !isTruncated) {
      setIsTruncated(true)
    }
  }, [activePlatform, isTruncated, setIsTruncated])

  const highlightClassNames: HighlightContentOptions = {
    linkClassName: Styles.highlight,
    mentionClassName: Styles.highlight,
    hashtagClassName: Styles.highlight,
  }

  const style: CSS.Properties = {
    ['--max-width' as any]: `${maxWidth}px`,
  }

  return (
    <div className={cx(Styles.container, containerClassName)} data-testid="x-preview" style={style}>
      <div className={Styles.avatar}>
        <img src={picture} alt={name} data-testid="x-avatar" />
      </div>
      <div className={Styles.main}>
        <div className={Styles.contentContainer}>
          <ConditionalWrapper
            condition={showHoverActionOverlay && almanacFileKey}
            wrapper={(children) => {
              if (!scheduledPost || !almanacFileKey) return children

              return (
                <EditCaptionOverlay
                  scheduledPost={scheduledPost}
                  almanacFileKey={almanacFileKey}
                  borderRadius={0}
                  position="top-right"
                  onClose={onClose}
                  padding={0}
                >
                  {children}
                </EditCaptionOverlay>
              )
            }}
          >
            <div className={Styles.headerContainer} ref={postPreviewHeaderRef}>
              <div className={Styles.header}>
                <div className={Styles.details}>
                  <NameAndHandle name={name} username={username} />
                  {onEdit && (
                    <div className={Styles.editButton}>
                      <Button variant="tertiary" onPress={onEdit} frontIcon={Edit1}>
                        Edit
                      </Button>
                    </div>
                  )}
                </div>
                {!hideConnectButton && isMobile && <ConnectButton margin="-6px -6px 0 auto" />}
              </div>
              <div className={Styles.content} data-testid="x-content" ref={contentRef}>
                {(() => {
                  if (isGenerating) return <CaptionSkeleton />
                  if (loading) {
                    return (
                      <>
                        {isMobile ? (
                          <PollingPreview
                            setImagesLoading={setImagesLoading}
                            imagesLoading={imagesLoading}
                            channel={ChannelType.Tweets}
                          />
                        ) : (
                          <Loading />
                        )}
                      </>
                    )
                  }
                  return (
                    <>
                      <span
                        ref={textRef as React.RefObject<HTMLSpanElement>}
                        key={`caption-${content}`}
                        className={cx({ [Styles.truncated]: isTruncated })}
                        dangerouslySetInnerHTML={{
                          __html: highlightContent(content, highlightClassNames),
                        }}
                      />
                      {shouldShowMoreButton && !alwaysTruncateCaption && (
                        <MoreButton
                          isTruncated={isTruncated}
                          setIsTruncated={() => {
                            setIsTruncated(!isTruncated)
                            onSeeMoreToggle?.(isTruncated)
                          }}
                        />
                      )}
                    </>
                  )
                })()}
              </div>
            </div>
          </ConditionalWrapper>
          {customPreviewContent || (
            <XPhotoBox
              mediaObjects={mediaObjects}
              loading={loading}
              imagesLoading={loading && imagesLoading}
              showVideoControls={showVideoControls}
              isActive={isActive}
              isInsertingMedia={isInsertingMedia}
              isPressable={!showHoverActionOverlay}
              showHoverActionOverlay={showHoverActionOverlay}
              almanacFileKey={almanacFileKey}
              onClose={onClose}
              isGenerating={isGenerating}
              content={content}
            />
          )}
        </div>
        <div className={Styles.actions} ref={postPreviewFooterRef}>
          <div className={Styles.action}>
            <Reply size="1.25em" />
            {isMobile ? null : 44}
          </div>
          <div className={Styles.action}>
            <Repost size="1.25em" />
            {isMobile ? null : '1.7K'}
          </div>
          <div className={Styles.action}>
            <Like size="1.25em" />
            {isMobile ? null : '31K'}
          </div>
          <div className={Styles.action}>
            <Views size="1.25em" />
            {isMobile ? null : '1.4M'}
          </div>
          <div className={Styles.rightActions}>
            <div className={Styles.action}>
              <Bookmark size="1.25em" />
            </div>
            <div className={Styles.action}>
              <Share size="1.25em" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const NameAndHandle = ({ name, username }: { name?: string; username?: string }) => {
  const { isMobile } = useResponsiveDesign()

  return isMobile ? (
    <div className={Styles.mobileNameAndHandle}>
      <span className={Styles.name} data-testid="x-name">
        {name}
      </span>
      <span className={Styles.handle} data-testid="x-username">
        @{username}
      </span>
      <span className={Styles.spacer}>•</span>
      <span className={Styles.timestamp}>30m</span>
    </div>
  ) : (
    <div className={Styles.nameAndHandle}>
      <div className={Styles.name} data-testid="x-name">
        {name}
      </div>
      <div className={Styles.handle} data-testid="x-username">
        @{username}
        <div className={Styles.spacer}>•</div>
        <div className={Styles.timestamp}>30m</div>
      </div>
    </div>
  )
}
